import { t } from 'i18next';
import z from 'zod';
import { numericString } from '../../lib/schemas/numeric-string';
import { countryCodes } from '../../utils/countries-iso3166';

const countriesSchema = z.union(countryCodes.map((c) => z.literal(c)));

export const MAX_NAME_LENGTH = 50;

const freeBand = z.object({ type: z.literal('free') });
const fixedPriceBand = z.object({
  type: z.literal('fixed_price'),
  price: numericString({ min: 0 }),
});
const percentageTotal = z.object({
  type: z.literal('percent_net_total'),
  percentageCharge: numericString({ min: 0 }),
});
const customBand = z.object({
  type: z.literal('custom'),
  costLanguageString: z.string(),
});

const noRequirement = z.object({ type: z.literal('none') });
const weightRequirement = z
  .object({
    type: z.literal('weight'),
    minGrams: numericString({ min: 0 }),
    maxGrams: numericString({ min: 0 }),
  })
  .refine((data) => data.maxGrams > data.minGrams, {
    message: t('errors:forms.min_exceeds_max'),
    path: ['maxGrams'], // Show error on `maxGrams` field
  });

const totalRequirement = z
  .object({
    type: z.literal('net_total'),
    minTotal: numericString({ min: 0 }),
    maxTotal: numericString({ min: 0 }),
  })
  .refine((data) => data.maxTotal > data.minTotal, {
    message: t('errors:forms.min_exceeds_max'),
    path: ['maxTotal'], // Show error on `maxTotal` field
  });

export const shippingBandCostSchema = z.discriminatedUnion('type', [
  freeBand,
  fixedPriceBand,
  percentageTotal,
  customBand,
]);

export const shippingBandRequirementSchema = z.discriminatedUnion('type', [
  noRequirement,
  weightRequirement,
  totalRequirement,
]);

const shippingBandBuilderSchema = z.object({
  key: z.string(), // Used only for front end to identify band uniquely
  name: z.string().nonempty().max(MAX_NAME_LENGTH),
  sku: z.string().optional(),
  priority: numericString({ min: 0 }),
  cost: shippingBandCostSchema,
  requirement: shippingBandRequirementSchema,
});

export const shippingMethodBuilderSchema = z.object({
  name: z.string().nonempty().max(MAX_NAME_LENGTH),
  sku: z.string().nonempty().max(MAX_NAME_LENGTH),
  priority: numericString({ min: 0, max: 127 }).refine(
    Number.isInteger,
    t('errors:forms.number.integer'),
  ),

  countries: z.array(z.string()).nonempty().pipe(z.array(countriesSchema)),
  regions: z.partialRecord(countriesSchema, z.array(z.string())),
  customerGroups: z.array(z.string()),
  bands: z.array(shippingBandBuilderSchema).nonempty(),
});

export type ShippingBandBuilderState = z.input<typeof shippingBandBuilderSchema>;

export type ShippingMethodBuilderState = z.input<typeof shippingMethodBuilderSchema>;
export type ShippingMethodBuilderValidated = z.output<typeof shippingMethodBuilderSchema>;
